import React from 'react';
import { isMobile } from 'react-device-detect';
import { ChatHero } from 'services/api/chatsApi';
import styled from 'styled-components';

interface HeroSectionProps {
  title: string;
  hero: ChatHero;
}

const HeroSection: React.FC<HeroSectionProps> = ({ title, hero }) => {
  const ctaButton = (
    <CTAButton
      as={hero.href ? "a" : "button"}
      href={hero.href}
      target={hero.href && hero.href.startsWith('http') ? "_blank" : undefined}
      rel={hero.href && hero.href.startsWith('http') ? "noopener noreferrer" : undefined}
    >
      {hero.buttonLabel || "Начать чат"}
    </CTAButton>
  );

  const heroImage = (
    <HeroImageBlock>
      <HeroImage
        src={hero.coverUrl || "/BG_2.webp"}
        alt={title}
      />
    </HeroImageBlock>
  );

  if (isMobile) {
    return (
      <HeroSectionWrapper>
        <MobileHeroContent>
          <HeroTextBlock>
            <HeroTitle>{title}</HeroTitle>
            <HeroDescription>{hero.description}</HeroDescription>
          </HeroTextBlock>
          {heroImage}
          {(hero.buttonLabel || hero.href) && ctaButton}
        </MobileHeroContent>
      </HeroSectionWrapper>
    );
  }

  return (
    <HeroSectionWrapper>
      <HeroContent>
        <HeroTextBlock>
          <HeroTitle>{title}</HeroTitle>
          <HeroDescription>{hero.description}</HeroDescription>
          {(hero.buttonLabel || hero.href) && ctaButton}
        </HeroTextBlock>
        {heroImage}
      </HeroContent>
    </HeroSectionWrapper>
  );
};

export default HeroSection;

const HeroSectionWrapper = styled.section`
  margin-bottom: 40px;
  
  @media (max-width: 968px) {
    margin-left: 16px;
    margin-right: 16px;
  }
`;

const HeroContent = styled.div`
  display: flex;
  align-items: center;
  gap: 0;
  padding: 32px;
  background: #1D1D1D;
  border-radius: 16px;
  backdrop-filter: blur(10px);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  max-height: 340px;
`;

const MobileHeroContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px;
  background: #1D1D1D;
  border-radius: 16px;
  backdrop-filter: blur(10px);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
`;

const HeroTextBlock = styled.div`
  flex: 2;
  padding-right: 20px;
  
  @media (max-width: 968px) {
    padding-right: 0;
    padding-bottom: 0;
    width: 100%;
  }
`;

const HeroTitle = styled.h1`
  font-size: 40px;
  line-height: 42px;
  font-family: Raleway;
  font-weight: 700;
  font-style: normal;
  color: #FFF;
  margin-bottom: 24px;

  @media (max-width: 720px) {
    font-size: 32px;
    line-height: 36px;
  }
`;

const HeroDescription = styled.p`
  font-family: "Nunito Sans";
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 18px;
  color: var(--Text-Color_text_primary, #FFF);
  margin-bottom: 24px;
  
  @media (max-width: 720px) {
    font-size: 14px;
    line-height: 16px;
  }
  
  @media (max-width: 968px) {
    margin-bottom: 0;
  }
`;

const CTAButton = styled.button`
  display: flex;
  height: 48px;
  padding: 16px 32px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  border-radius: 64px;
  background: #FF7E55;
  box-shadow: 0px 12px 40px 0px rgba(255, 126, 85, 0.12);
  color: white;
  border: none;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;
  width: max-content;
  
  @media (max-width: 968px) {
    width: 100%;
  }
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0px 16px 50px 0px rgba(255, 126, 85, 0.20);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const HeroImageBlock = styled.div`
  flex: 1;
  max-width: 400px;
  margin: -32px -32px -32px auto;
  
  @media (max-width: 968px) {
    margin: 0;
    max-width: none;
    width: 100%;
  }
`;

const HeroImage = styled.img`
  width: 100%;
  height: 276px;
  object-fit: cover;
  border-radius: 0 16px 16px 0;
  background: #2A2A2A;
  
  @media (max-width: 968px) {
    height: 300px;
    border-radius: 16px;
  }
`; 